import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getProfileByUid,
  getProfileByUsername,
  updateProfileData,
  uploadProfilePhoto,
  uploadCoverPhoto,
  updateMartialArt,
  removeMartialArt,
  getFriends,
  getFullFriendProfiles,
  getUserProfilesByUids,
  getFriendRequests,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  undoDeclineFriendRequest,
  removeFriend,
  UserProfile,
  UserProfileUpdateData,
  UserSearchResult
} from '../../services/firebase/profile.service';
import { getSuggestedFriends } from '../../services/firebase/friend-suggestion.service';
import logger from '../../utils/logger';

// Types
interface UserState {
  currentProfile: UserProfile | null;
  viewedProfiles: Record<string, UserProfile>;
  friendsMap: Record<string, UserSearchResult>;
  friendsList: string[];
  friendRequests: Record<string, any>;
  pendingFriendRequests: {
    sent: any[];
    received: any[];
    declined: any[];
  };
  suggestedFriends: UserSearchResult[];
  searchResults: UserSearchResult[];
  isLoading: boolean;
  error: string | null;
  lastFriendRequestFetch: number | null;
  isFetchingFriendRequests: boolean;
}

// Initial state
const initialState: UserState = {
  currentProfile: null,
  viewedProfiles: {},
  friendsMap: {},
  friendsList: [],
  friendRequests: {},
  pendingFriendRequests: {
    sent: [],
    received: [],
    declined: []
  },
  suggestedFriends: [],
  searchResults: [],
  isLoading: false,
  error: null,
  lastFriendRequestFetch: null,
  isFetchingFriendRequests: false
};

// Async thunks
export const fetchProfileByUid = createAsyncThunk<
  UserProfile,
  string,
  { rejectValue: string; fulfilledMeta: { isAuthUser: boolean } }
>('user/fetchProfileByUid', async (uid, { rejectWithValue, getState, fulfillWithValue }) => {
  try {
    const profile = await getProfileByUid(uid);
    if (!profile) {
      return rejectWithValue('Profile not found');
    }
    // Tell the reducer whether this is the authenticated user's own profile,
    // so currentProfile can't be claimed by whichever profile loads first
    const authUid = (getState() as { auth?: { user?: { uid?: string } | null } }).auth?.user?.uid;
    return fulfillWithValue(profile, { isAuthUser: !!authUid && profile.uid === authUid });
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch profile');
  }
});

export const fetchProfileByUsername = createAsyncThunk<
  UserProfile,
  string,
  { rejectValue: string }
>('user/fetchProfileByUsername', async (username, { rejectWithValue }) => {
  try {
    const profile = await getProfileByUsername(username);
    if (!profile) {
      return rejectWithValue('Profile not found');
    }
    return profile;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch profile');
  }
});

export const updateProfile = createAsyncThunk<
  UserProfile,
  { uid: string; data: UserProfileUpdateData },
  { rejectValue: string }
>('user/updateProfile', async ({ uid, data }, { rejectWithValue }) => {
  try {
    const profile = await updateProfileData(uid, data);
    return profile;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update profile');
  }
});

export const uploadUserPhoto = createAsyncThunk<
  { photoURL: string; uid: string },
  { uid: string; file: File },
  { rejectValue: string }
>('user/uploadUserPhoto', async ({ uid, file }, { rejectWithValue }) => {
  try {
    const photoURL = await uploadProfilePhoto(uid, file);
    return { photoURL, uid };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to upload profile photo');
  }
});

export const uploadUserCoverPhoto = createAsyncThunk<
  { coverPhotoURL: string; aspectRatio: number; uid: string },
  { uid: string; file: File },
  { rejectValue: string }
>('user/uploadUserCoverPhoto', async ({ uid, file }, { rejectWithValue }) => {
  try {
    const result = await uploadCoverPhoto(uid, file);
    return { coverPhotoURL: result.url, aspectRatio: result.aspectRatio, uid };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to upload cover photo');
  }
});

export const updateUserMartialArt = createAsyncThunk<
  UserProfile,
  {
    uid: string;
    martialArtId: string;
    data: {
      name: string;
      startDate: number;
      experience: number;
      rank?: string;
      instructor?: string;
    }
  },
  { rejectValue: string }
>('user/updateUserMartialArt', async ({ uid, martialArtId, data }, { rejectWithValue }) => {
  try {
    const profile = await updateMartialArt(uid, martialArtId, data);
    return profile;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update martial art');
  }
});

export const removeUserMartialArt = createAsyncThunk<
  UserProfile,
  { uid: string; martialArtId: string },
  { rejectValue: string }
>('user/removeUserMartialArt', async ({ uid, martialArtId }, { rejectWithValue }) => {
  try {
    const profile = await removeMartialArt(uid, martialArtId);
    return profile;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to remove martial art');
  }
});

export const searchForUsers = createAsyncThunk<
  UserSearchResult[],
  { searchTerm: string; limit?: number },
  { rejectValue: string }
>('user/searchForUsers', async ({ searchTerm, limit }, { rejectWithValue }) => {
  try {
    logger.info(`Searching for users with term: ${searchTerm}`);
    
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }
    
    // Import the main search service that handles both PostgreSQL and Firebase fallback
    const { searchService } = await import('../../services/search.service');
    
    // Initialize the search service if needed
    const isInitialized = await searchService.initialize();
    logger.info('Search service initialized:', isInitialized);
    
    // Call the search service - it will handle PostgreSQL first, then fallback to Firebase
    try {
      logger.info(`Searching for users via search service: ${searchTerm}`);
      const results = await searchService.searchUsers(searchTerm, limit || 20);
      
      logger.info(`Search service returned ${results.length} results`);
      
      // The search service already returns properly formatted UserSearchResult[]
      // but we need to ensure consistency
      return results.map((user: any) => {
        // Ensure we have both uid and id fields
        const userId = user.uid || user.id || `user-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
        
        return {
          uid: userId,
          id: userId,
          username: user.username || '',
          displayName: user.displayName || user.display_name || '',
          photoURL: user.photoURL || user.photo_url || null,
          isInstructor: user.isInstructor || false
        };
      });
    } catch (searchError) {
      logger.error('Search service error, trying direct API search:', searchError);
      
      // If the main search service fails, try the direct API search as a backup
      const apiSearchService = (await import('../../services/api/search.service')).default;
      
      try {
        const response = await apiSearchService.searchUsersByName({
          query: searchTerm,
          limit: limit
        });
        
        if (response && response.results && response.results.length > 0) {
          logger.info(`Found ${response.results.length} users via direct API search`);
          
          return response.results.map(user => {
            const userId = user.id || user.uid || `user-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
            
            return {
              uid: userId,
              id: userId,
              username: user.username, 
              displayName: user.display_name,
              photoURL: user.photo_url,
              isInstructor: user.isInstructor || false
            };
          });
        }
        logger.info('PostgreSQL search returned no results');
        return [];
      } catch (apiError) {
        logger.error('Direct API search error:', apiError);
        throw apiError;
      }
    }
  } catch (error: any) {
    logger.error('Search failed:', error);
    
    // Only fallback to Firebase in development environment or if specifically configured
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENABLE_FIREBASE_SEARCH === 'true') {
      // Use Firebase search as fallback
      try {
        logger.info('Using Firebase as fallback for search');
        const { searchUsers } = await import('../../services/firebase/profile.service');
        const firebaseResults = await searchUsers(searchTerm, limit);
        return firebaseResults;
      } catch (fbError) {
        logger.error('Firebase fallback search failed:', fbError);
        return [];
      }
    }
    
    // Return empty results if all search methods failed
    return [];
  }
});

export const fetchUserFriends = createAsyncThunk<
  UserSearchResult[],
  string,
  { rejectValue: string }
>('user/fetchUserFriends', async (uid, { rejectWithValue }) => {
  try {
    const friends = await getFriends(uid);
    return friends;
  } catch (error: any) {
    logger.error('Error fetching friends:', error);
    // Don't fail completely if friends can't be fetched
    // Return empty array so feed can still show user's own posts
    return [];
  }
});

// This thunk fetches complete profile data for all friends of a user
export const fetchFullFriendProfiles = createAsyncThunk<
  Record<string, UserProfile>,
  string,
  { rejectValue: string }
>('user/fetchFullFriendProfiles', async (uid, { rejectWithValue }) => {
  try {
    // logger.info(`Fetching full friend profiles for user ${uid}`);
    const friendProfiles = await getFullFriendProfiles(uid);
    // logger.info(`Fetched ${Object.keys(friendProfiles).length} full friend profiles`);
    return friendProfiles;
  } catch (error: any) {
    logger.error('Error fetching full friend profiles:', error);
    // Don't completely fail - return empty object so feed can still work
    // The feed will just show less information for friend posts
    return {};
  }
});

// This thunk fetches profile data for specified user IDs who aren't friends
export const fetchProfilesByUids = createAsyncThunk<
  Record<string, UserProfile>,
  string[],
  { rejectValue: string }
>('user/fetchProfilesByUids', async (uids, { rejectWithValue }) => {
  try {
    // logger.info(`Fetching profiles for ${uids.length} users`);
    const profiles = await getUserProfilesByUids(uids);
    // logger.info(`Fetched ${Object.keys(profiles).length} profiles`);
    return profiles;
  } catch (error: any) {
    logger.error('Error fetching profiles by UIDs:', error);
    return rejectWithValue(error.message || 'Failed to fetch profiles by UIDs');
  }
});

export const fetchUserFriendRequests = createAsyncThunk<
  Record<string, any>,
  string,
  { rejectValue: string; state: { user: UserState } }
>('user/fetchUserFriendRequests', async (uid, { rejectWithValue, getState }) => {
  try {
    const state = getState();

    // Check if we've fetched recently (within 5 seconds).
    // Do NOT guard on isFetchingFriendRequests here: RTK dispatches `pending`
    // (which sets that flag) before this creator runs, so the guard was
    // always true and friend requests were never actually fetched.
    const now = Date.now();
    if (state.user.lastFriendRequestFetch && (now - state.user.lastFriendRequestFetch) < 5000) {
      return state.user.friendRequests; // Return cached data
    }

    const requests = await getFriendRequests(uid);
    return requests;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch friend requests');
  }
});

export const sendUserFriendRequest = createAsyncThunk<
  { fromUid: string; toUid: string },
  { fromUid: string; toUid: string },
  { rejectValue: string }
>('user/sendUserFriendRequest', async ({ fromUid, toUid }, { rejectWithValue }) => {
  try {
    await sendFriendRequest(fromUid, toUid);
    return { fromUid, toUid };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to send friend request');
  }
});

export const acceptUserFriendRequest = createAsyncThunk<
  { currentUid: string; requestorUid: string },
  { currentUid: string; requestorUid: string },
  { rejectValue: string }
>('user/acceptUserFriendRequest', async ({ currentUid, requestorUid }, { rejectWithValue }) => {
  try {
    await acceptFriendRequest(currentUid, requestorUid);
    return { currentUid, requestorUid };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to accept friend request');
  }
});

export const declineUserFriendRequest = createAsyncThunk<
  { currentUid: string; requestorUid: string },
  { currentUid: string; requestorUid: string },
  { rejectValue: string }
>('user/declineUserFriendRequest', async ({ currentUid, requestorUid }, { rejectWithValue }) => {
  try {
    await declineFriendRequest(currentUid, requestorUid);
    return { currentUid, requestorUid };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to decline friend request');
  }
});

export const undoUserDeclinedFriendRequest = createAsyncThunk<
  { currentUid: string; declinedUid: string },
  { currentUid: string; declinedUid: string },
  { rejectValue: string }
>('user/undoUserDeclinedFriendRequest', async ({ currentUid, declinedUid }, { rejectWithValue }) => {
  try {
    await undoDeclineFriendRequest(currentUid, declinedUid);
    return { currentUid, declinedUid };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to undo declined friend request');
  }
});

export const removeUserFriend = createAsyncThunk<
  { currentUid: string; friendUid: string },
  { currentUid: string; friendUid: string },
  { rejectValue: string }
>('user/removeUserFriend', async ({ currentUid, friendUid }, { rejectWithValue }) => {
  try {
    await removeFriend(currentUid, friendUid);
    return { currentUid, friendUid };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to remove friend');
  }
});

// Fetch suggested friends
export const fetchSuggestedFriends = createAsyncThunk<
  UserSearchResult[],
  { uid: string; limit?: number },
  { rejectValue: string }
>('user/fetchSuggestedFriends', async ({ uid, limit }, { rejectWithValue }) => {
  try {
    const suggestions = await getSuggestedFriends(uid, limit);
    return suggestions;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch suggested friends');
  }
});

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    clearViewedProfiles: (state) => {
      state.viewedProfiles = {};
    },
    updateUserProfile: (state, action: PayloadAction<{ userId: string; updates: Partial<UserProfile> }>) => {
      const { userId, updates } = action.payload;
      
      // Update in viewed profiles
      if (state.viewedProfiles[userId]) {
        state.viewedProfiles[userId] = { ...state.viewedProfiles[userId], ...updates };
      }
      
      // If this is the current user, also update currentProfile
      if (state.currentProfile && userId === state.currentProfile.uid) {
        state.currentProfile = { ...state.currentProfile, ...updates };
      }
    },
    setFriendRequests: (state, action: PayloadAction<Record<string, any>>) => {
      state.friendRequests = action.payload;
      // Set pendingFriendRequests for backward compatibility
      state.pendingFriendRequests = {
        sent: action.payload.sent || [],
        received: action.payload.received || [],
        declined: action.payload.declined || []
      };
      state.lastFriendRequestFetch = Date.now();
    },
    // Set the current user's profile directly (used by FirebaseProvider after login)
    setCurrentProfile: (state, action: PayloadAction<UserProfile>) => {
      state.currentProfile = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch profile by UID
    builder.addCase(fetchProfileByUid.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProfileByUid.fulfilled, (state, action) => {
      state.isLoading = false;
      // logger.info('Profile fetched in reducer:', action.payload);
      
      // Store in viewed profiles
      state.viewedProfiles[action.payload.uid] = action.payload;

      // Only the authenticated user's own profile may become currentProfile.
      // The previous first-profile-wins check let a deep-linked stranger's
      // profile claim currentProfile for the whole session, corrupting
      // isAdmin checks and belt display.
      if (action.meta.isAuthUser || action.payload.uid === state.currentProfile?.uid) {
        state.currentProfile = action.payload;
      }
    });
    builder.addCase(fetchProfileByUid.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch profile';
    });

    // Fetch profile by username
    builder.addCase(fetchProfileByUsername.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProfileByUsername.fulfilled, (state, action) => {
      state.isLoading = false;
      // Store in viewed profiles
      state.viewedProfiles[action.payload.uid] = action.payload;
      // If this is the current user, also update currentProfile
      if (state.currentProfile && action.payload.uid === state.currentProfile.uid) {
        state.currentProfile = action.payload;
      }
    });
    builder.addCase(fetchProfileByUsername.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch profile';
    });

    // Update profile
    builder.addCase(updateProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update in viewed profiles
      state.viewedProfiles[action.payload.uid] = action.payload;
      // If this is the current user, also update currentProfile
      if (state.currentProfile && action.payload.uid === state.currentProfile.uid) {
        state.currentProfile = action.payload;
      }
    });
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update profile';
    });

    // Upload profile photo
    builder.addCase(uploadUserPhoto.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(uploadUserPhoto.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update photo URL in viewed profiles
      if (state.viewedProfiles[action.payload.uid]) {
        state.viewedProfiles[action.payload.uid].photoURL = action.payload.photoURL;
      }
      // If this is the current user, also update currentProfile
      if (state.currentProfile && action.payload.uid === state.currentProfile.uid) {
        state.currentProfile.photoURL = action.payload.photoURL;
      }
    });
    builder.addCase(uploadUserPhoto.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to upload profile photo';
    });

    // Upload cover photo
    builder.addCase(uploadUserCoverPhoto.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(uploadUserCoverPhoto.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update cover photo URL in viewed profiles
      if (state.viewedProfiles[action.payload.uid]) {
        state.viewedProfiles[action.payload.uid].coverPhotoURL = action.payload.coverPhotoURL;
      }
      // If this is the current user, also update currentProfile
      if (state.currentProfile && action.payload.uid === state.currentProfile.uid) {
        state.currentProfile.coverPhotoURL = action.payload.coverPhotoURL;
      }
    });
    builder.addCase(uploadUserCoverPhoto.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to upload cover photo';
    });

    // Update martial art
    builder.addCase(updateUserMartialArt.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateUserMartialArt.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update in viewed profiles
      state.viewedProfiles[action.payload.uid] = action.payload;
      // If this is the current user, also update currentProfile
      if (state.currentProfile && action.payload.uid === state.currentProfile.uid) {
        state.currentProfile = action.payload;
      }
    });
    builder.addCase(updateUserMartialArt.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update martial art';
    });

    // Remove martial art
    builder.addCase(removeUserMartialArt.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(removeUserMartialArt.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update in viewed profiles
      state.viewedProfiles[action.payload.uid] = action.payload;
      // If this is the current user, also update currentProfile
      if (state.currentProfile && action.payload.uid === state.currentProfile.uid) {
        state.currentProfile = action.payload;
      }
    });
    builder.addCase(removeUserMartialArt.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to remove martial art';
    });

    // Search users
    builder.addCase(searchForUsers.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(searchForUsers.fulfilled, (state, action) => {
      state.isLoading = false;
      state.searchResults = action.payload;
    });
    builder.addCase(searchForUsers.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to search users';
    });

    // Fetch friends
    builder.addCase(fetchUserFriends.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchUserFriends.fulfilled, (state, action) => {
      state.isLoading = false;
      // QM2: merge, don't replace. HomePage dispatches this AND
      // fetchFullFriendProfiles in parallel; a full replace here would
      // clobber the richer profiles (isPeakSub/isInstructor) that the other
      // thunk wrote, making Peak/instructor badges flicker per load. Spread
      // the lean entry FIRST so any existing richer fields win.
      action.payload.forEach(friend => {
        state.friendsMap[friend.uid] = { ...friend, ...state.friendsMap[friend.uid] };
        if (!state.friendsList.includes(friend.uid)) {
          state.friendsList.push(friend.uid);
        }
      });
    });
    builder.addCase(fetchUserFriends.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch friends';
    })
    
    // Fetch full friend profiles
    .addCase(fetchFullFriendProfiles.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    })
    .addCase(fetchFullFriendProfiles.fulfilled, (state, action) => {
      state.isLoading = false;

      // QM2: merge, don't replace. This is the RICHER source (isPeakSub/
      // isInstructor), so spread it LAST — it wins per-field over anything
      // fetchUserFriends wrote, in either resolution order.
      Object.entries(action.payload).forEach(([friendId, profile]) => {
        const richEntry = {
          uid: friendId,
          id: friendId, // Add id field for compatibility
          displayName: profile.displayName || `${profile.firstName} ${profile.lastName}`,
          username: profile.username,
          profilePhotoURL: profile.profilePhotoURL || profile.photoURL,
          isInstructor: profile.isInstructor,
          isPeakSub: profile.isPeakSub // Include Peak subscription status
        };
        state.friendsMap[friendId] = { ...state.friendsMap[friendId], ...richEntry };

        // Also store in viewedProfiles for complete access
        state.viewedProfiles[friendId] = profile;

        if (!state.friendsList.includes(friendId)) {
          state.friendsList.push(friendId);
        }
      });
    })
    .addCase(fetchFullFriendProfiles.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch full friend profiles';
    })
    
    // Fetch profiles by UIDs
    .addCase(fetchProfilesByUids.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    })
    .addCase(fetchProfilesByUids.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Update viewedProfiles with the fetched profiles
      Object.entries(action.payload).forEach(([uid, profile]) => {
        state.viewedProfiles[uid] = profile;
      });
      
      // logger.info(`Added ${Object.keys(action.payload).length} profiles to viewedProfiles`);
    })
    .addCase(fetchProfilesByUids.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch profiles by UIDs';
    })

    // Fetch suggested friends
    .addCase(fetchSuggestedFriends.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    })
    .addCase(fetchSuggestedFriends.fulfilled, (state, action) => {
      state.isLoading = false;
      state.suggestedFriends = action.payload;
    })
    .addCase(fetchSuggestedFriends.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch suggested friends';
    });

    // Fetch friend requests
    builder.addCase(fetchUserFriendRequests.pending, (state) => {
      state.isFetchingFriendRequests = true;
      state.error = null;
    });
    builder.addCase(fetchUserFriendRequests.fulfilled, (state, action) => {
      state.isFetchingFriendRequests = false;
      state.lastFriendRequestFetch = Date.now();
      state.friendRequests = action.payload;
      // Set pendingFriendRequests for backward compatibility
      state.pendingFriendRequests = {
        sent: action.payload.sent || [],
        received: action.payload.received || [],
        declined: action.payload.declined || []
      };
    });
    builder.addCase(fetchUserFriendRequests.rejected, (state, action) => {
      state.isFetchingFriendRequests = false;
      state.error = action.payload || 'Failed to fetch friend requests';
    });

    // Send friend request
    builder.addCase(sendUserFriendRequest.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(sendUserFriendRequest.fulfilled, (state) => {
      state.isLoading = false;
      // We don't need to update any state here as the recipient will receive this
    });
    builder.addCase(sendUserFriendRequest.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to send friend request';
    });

    // Accept friend request
    builder.addCase(acceptUserFriendRequest.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(acceptUserFriendRequest.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update friend requests
      const { requestorUid } = action.payload;

      if (state.friendRequests[requestorUid]) {
        state.friendRequests[requestorUid].status = 'accepted';
      }

      // QM3: splice the accepted request out of the received list. The decline
      // handler does this, but accept previously only flipped `status`, so an
      // accepted request stayed visible in the pending-received UI until a full
      // refetch. (No "accepted" bucket to move to — the new friend now lives in
      // friendsMap.)
      state.pendingFriendRequests.received = state.pendingFriendRequests.received.filter(
        req => req.senderId !== requestorUid && req.id !== requestorUid
      );
    });
    builder.addCase(acceptUserFriendRequest.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to accept friend request';
    });

    // Decline friend request
    builder.addCase(declineUserFriendRequest.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(declineUserFriendRequest.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update friend requests
      const { requestorUid } = action.payload;

      // Remove from pending requests
      if (state.friendRequests[requestorUid]) {
        state.friendRequests[requestorUid].status = 'declined';
      }

      // Move from received to declined in pendingFriendRequests
      const declinedRequest = state.pendingFriendRequests.received.find(
        req => req.senderId === requestorUid || req.id === requestorUid
      );
      if (declinedRequest) {
        state.pendingFriendRequests.received = state.pendingFriendRequests.received.filter(
          req => req.senderId !== requestorUid && req.id !== requestorUid
        );
        state.pendingFriendRequests.declined.push({
          ...declinedRequest,
          status: 'declined'
        });
      }
    });
    builder.addCase(declineUserFriendRequest.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to decline friend request';
    });

    // Undo declined friend request
    builder.addCase(undoUserDeclinedFriendRequest.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(undoUserDeclinedFriendRequest.fulfilled, (state, action) => {
      state.isLoading = false;
      const { declinedUid } = action.payload;

      // Remove from declined list
      state.pendingFriendRequests.declined = state.pendingFriendRequests.declined.filter(
        req => req.senderId !== declinedUid && req.id !== declinedUid
      );

      // Add to friends list
      if (!state.friendsList.includes(declinedUid)) {
        state.friendsList.push(declinedUid);
      }

      // Update friend requests status
      if (state.friendRequests[declinedUid]) {
        state.friendRequests[declinedUid].status = 'accepted';
      }
    });
    builder.addCase(undoUserDeclinedFriendRequest.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to undo declined friend request';
    });

    // Remove friend
    builder.addCase(removeUserFriend.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(removeUserFriend.fulfilled, (state, action) => {
      state.isLoading = false;
      // Remove from friends list and map
      const { friendUid } = action.payload;
      
      delete state.friendsMap[friendUid];
      state.friendsList = state.friendsList.filter(uid => uid !== friendUid);
    });
    builder.addCase(removeUserFriend.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to remove friend';
    });
  }
});

export const { clearSearchResults, clearError, clearViewedProfiles, updateUserProfile, setFriendRequests, setCurrentProfile } = userSlice.actions;

export default userSlice.reducer;